import { useMemo, useEffect } from "react";
import { useOperator } from "@/hooks";
import { useGetProviderSubscriptionsQuery, useGetProviderPaymentMethodsQuery } from "@/redux/apis/subscription/subscriptionPlansApi";
import { SubscriptionAssignment } from "@/redux/apis/subscription/types";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import {
  markWelcomeModalShown,
  markPaymentMethodsLoaded,
  openPlanSelectionModal,
  openPlanSelectionModalWithPlan,
  closePlanSelectionModal,
  hasShownWelcomeModalSelector,
  showPlanSelectionModalSelector,
  preSelectedPlanIdSelector,
  hasEverLoadedPaymentMethodsSelector,
} from "@/redux/slices/subscriptionAssignment";

// Helper function to calculate days left
const calculateDaysLeft = (trialEndsAt: string | null | undefined): number | null => {
  if (!trialEndsAt) return null;

  const trialEnd = new Date(trialEndsAt);
  const now = new Date();
  const diffTime = trialEnd.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return Math.max(0, diffDays);
};

/**
 * Hook to manage subscription assignment state
 *
 * This hook:
 * 1. Loads subscription assignments from the API
 * 2. Loads payment methods to determine welcome modal visibility
 * 3. Provides computed subscription status for UI decisions
 * 4. Provides actions to mark modals as shown
 */
export const useSubscriptionAssignment = () => {
  const dispatch = useAppDispatch();
  const operator = useOperator();
  const hasShownWelcomeModal = useAppSelector(hasShownWelcomeModalSelector);
  const manuallyOpenedPlanSelection = useAppSelector(showPlanSelectionModalSelector);
  const preSelectedPlanId = useAppSelector(preSelectedPlanIdSelector);
  const hasEverLoadedPaymentMethods = useAppSelector(hasEverLoadedPaymentMethodsSelector);

  // Fetch provider subscriptions
  const {
    data: subscriptions,
    isLoading: isLoadingSubscriptions,
    error: subscriptionsError,
  } = useGetProviderSubscriptionsQuery(
    {
      providerId: operator?.id ?? "",
      // "overdue" is a payment-grace state, NOT unsubscribed: the provider
      // still has a plan, so the UI must keep showing it (DEV-1395). Without
      // it an overdue provider resolves no baseSubscription and renders as
      // unsubscribed.
      status: ["active", "pending_cancellation", "trial", "overdue"],
      limit: 1,
      orderBy: ["startDate:DESC"],
    },
    { skip: !operator?.id }
  );

  // Fetch provider payment methods
  // isLoading: true only on initial load (no cached data)
  // isFetching: true on initial load AND refetches
  const {
    data: paymentMethods,
    isLoading: isLoadingPaymentMethods,
    isFetching: isFetchingPaymentMethods,
    error: paymentMethodsError,
    isSuccess: paymentMethodsSuccess,
  } = useGetProviderPaymentMethodsQuery({ providerId: operator?.id ?? "" }, { skip: !operator?.id });

  // Dispatch action to mark payment methods as loaded when they successfully load
  // This persists the flag in Redux (and localStorage via redux-persist)
  useEffect(() => {
    if (paymentMethodsSuccess && !paymentMethodsError) {
      dispatch(markPaymentMethodsLoaded());
    }
  }, [paymentMethodsSuccess, paymentMethodsError, dispatch]);

  // Compute subscription status
  const subscriptionStatus = useMemo(() => {
    const baseSubscription = subscriptions?.[0] ?? null;

    // Only determine hasPaymentMethod after loading is complete AND there's no error
    // Use isFetching (not isLoading) to check current state, as isLoading is only true on initial load
    const hasPaymentMethod = !isFetchingPaymentMethods && !paymentMethodsError && (paymentMethods?.length ?? 0) > 0;
    // Can determine user type if:
    // 1. Payment methods have successfully loaded at least once (hasEverLoadedPaymentMethods from persisted state)
    // 2. Initial load is complete (!isLoadingPaymentMethods) - prevents flash on hard refresh
    // 3. No errors
    // Note: We check isLoading (not isFetching) to allow using cached data during refetches
    const canDetermineUserType = hasEverLoadedPaymentMethods && !isLoadingPaymentMethods && !paymentMethodsError;

    // If no base subscription, return empty status
    if (!baseSubscription) {
      return {
        baseSubscription: null,
        addOns: [] as SubscriptionAssignment[],
        isInTrial: false,
        trialDaysLeft: null,
        trialEndsAt: null,
        userType: "new" as const,
        isActive: false,
        isExpired: false,
        isCancelled: false,
        hasPaymentMethod,
        shouldShowWelcomeModal: false,
        shouldShowTrialBanner: false,
        shouldShowPlanSelectionModal: false,
        planSelectionModalVariant: null,
      };
    }

    const isInTrial = baseSubscription.isTrialPeriod;
    const trialDaysLeft = calculateDaysLeft(baseSubscription.trialEndsAt);
    const trialEndsAt = baseSubscription.trialEndsAt ?? null;

    // User type: "existing" if no payment method (migrated users), "new" if has payment method
    const userType: "new" | "existing" = hasPaymentMethod ? "new" : "existing";

    const isActive = baseSubscription.status === "active" || baseSubscription.status === "trial";
    const isExpired = baseSubscription.status === "expired";
    const isCancelled = baseSubscription.status === "cancelled";

    // UI Logic for UpgradedPlanWelcomeModal:
    // Show only if no payment method attached AND hasn't been shown yet AND we can determine user type
    // Don't show modal while payment methods are still loading (prevents flash during initial load)
    const shouldShowWelcomeModal = !hasPaymentMethod && !hasShownWelcomeModal && canDetermineUserType;

    // UI Logic for TrialSubscriptionBanner:
    // Show only when provider is in a trial period AND we can determine user type
    // Don't show banner if payment methods API failed (can't reliably determine user type)
    const shouldShowTrialBanner = isInTrial && canDetermineUserType;

    // UI Logic for PlanSelectionModal:
    // - "activate": User wants to activate their plan early (triggered by button click)
    // - "trial-ended": Trial has ended for users without payment method
    // - "subscription-ended": Subscription was cancelled or ended
    let shouldShowPlanSelectionModal = false;
    let planSelectionModalVariant: "activate" | "trial-ended" | "subscription-ended" | null = null;

    // Only auto-show for users without payment method
    if (!hasPaymentMethod) {
      if (isExpired && trialEndsAt) {
        shouldShowPlanSelectionModal = true;
        planSelectionModalVariant = "trial-ended";
      } else if (isCancelled || (isExpired && !trialEndsAt)) {
        shouldShowPlanSelectionModal = true;
        planSelectionModalVariant = "subscription-ended";
      }
    }

    return {
      baseSubscription,
      addOns: [] as SubscriptionAssignment[],
      isInTrial,
      trialDaysLeft,
      trialEndsAt,
      userType,
      isActive,
      isExpired,
      isCancelled,
      hasPaymentMethod,
      shouldShowWelcomeModal,
      shouldShowTrialBanner,
      shouldShowPlanSelectionModal,
      planSelectionModalVariant,
    };
  }, [
    subscriptions,
    paymentMethods,
    isLoadingPaymentMethods,
    isFetchingPaymentMethods,
    paymentMethodsError,
    hasShownWelcomeModal,
    hasEverLoadedPaymentMethods,
  ]);

  // Combined plan selection modal state
  const planSelectionModal = useMemo(() => {
    if (manuallyOpenedPlanSelection) {
      return {
        shouldShow: true,
        variant: "activate" as const,
      };
    }

    return {
      shouldShow: subscriptionStatus.shouldShowPlanSelectionModal,
      variant: subscriptionStatus.planSelectionModalVariant,
    };
  }, [manuallyOpenedPlanSelection, subscriptionStatus]);

  const markWelcomeModalAsShown = () => {
    dispatch(markWelcomeModalShown());
  };

  const openPlanSelection = () => {
    dispatch(openPlanSelectionModal());
  };

  const openPlanSelectionWithPlan = (planId: string) => {
    dispatch(openPlanSelectionModalWithPlan(planId));
  };

  const closePlanSelection = () => {
    dispatch(closePlanSelectionModal());
  };

  return {
    subscriptionStatus,
    planSelectionModal,
    preSelectedPlanId,
    isLoading: isLoadingSubscriptions || isLoadingPaymentMethods,
    isLoadingSubscriptions,
    isLoadingPaymentMethods,
    error: subscriptionsError,
    markWelcomeModalAsShown,
    openPlanSelection,
    openPlanSelectionWithPlan,
    closePlanSelection,
  };
};
